import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import type { Dayjs } from 'dayjs';

dayjs.extend(customParseFormat);

export const DATE_PICKER_FORMAT = 'MM.DD.YYYY';

export const parseTypedDate = (value: string): Dayjs | null => {
  const parsed = dayjs(value, DATE_PICKER_FORMAT, true);

  return parsed.isValid() ? parsed : null;
};

export const formatDateValue = (date: Dayjs): string =>
  date.format(DATE_PICKER_FORMAT);

/** Converts a backend ISO date (`YYYY-MM-DD`) into this picker's `mm.dd.yyyy` display format. */
export const formatIsoToDisplay = (iso: string | null | undefined): string => {
  if (!iso) {
    return '';
  }

  const parsed = dayjs(iso);

  return parsed.isValid() ? parsed.format(DATE_PICKER_FORMAT) : '';
};

/** Converts this picker's typed `mm.dd.yyyy` value into the ISO date the backend expects, or `null` if unparseable. */
export const formatDisplayToIso = (value: string): string | null => {
  const parsed = parseTypedDate(value);

  return parsed ? parsed.format('YYYY-MM-DD') : null;
};

const MAX_MONTH = 12;
const DATE_SEGMENT_LENGTH = 2;
const YEAR_SEGMENT_LENGTH = 4;
const LEAP_YEAR_FALLBACK = 2000;

const getDaysInMonth = (month: number, yearPart: string): number => {
  const year =
    yearPart.length === YEAR_SEGMENT_LENGTH
      ? Number(yearPart)
      : LEAP_YEAR_FALLBACK;

  // `.date(1)` must be set before `.year()`/`.month()` — otherwise dayjs keeps
  // today's day-of-month while switching month and can overflow into the next one.
  return dayjs()
    .date(1)
    .year(year)
    .month(month - 1)
    .daysInMonth();
};

export const getDateFormatError = (value: string): string | null => {
  const [monthPart = '', dayPart = '', yearPart = ''] = value.split('.');

  const isMonthTyped = monthPart.length === DATE_SEGMENT_LENGTH;
  const isDayTyped = dayPart.length === DATE_SEGMENT_LENGTH;

  if (
    isMonthTyped &&
    (Number(monthPart) === 0 || Number(monthPart) > MAX_MONTH)
  ) {
    return `Month must be between 01 and ${MAX_MONTH}`;
  }

  if (isDayTyped && Number(dayPart) === 0) {
    return 'Day must be at least 01';
  }

  if (isDayTyped && isMonthTyped) {
    const month = Number(monthPart);
    const maxDayInMonth = getDaysInMonth(month, yearPart);

    if (Number(dayPart) > maxDayInMonth) {
      const monthName = dayjs()
        .date(1)
        .month(month - 1)
        .format('MMMM');

      return `${monthName} has only ${maxDayInMonth} days`;
    }
  }

  return null;
};
